/**
 * Modal со списком медиа конкретного пользователя — TG-стиль
 * (Photos / Files / Voice messages / Shared links).
 *
 * Открывается из ProfileLightbox по клику на счётчик статистики.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { fetchUserMedia, chatAttachmentUrl, deleteChatMessage, type UserMediaItem } from "@/api/dashboard";
import { VoiceMessage } from "@/components/VoiceMessage";
import { useAuth } from "@/auth/AuthContext";
import { parseBackendIso } from "@/lib/presence";


function fmtSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}


function fmtMonth(iso: string): string {
  const d = parseBackendIso(iso);
  if (!d || Number.isNaN(d.getTime())) return "";
  const months = ["январь","февраль","март","апрель","май","июнь","июль","август","сентябрь","октябрь","ноябрь","декабрь"];
  const yr = d.getFullYear();
  const now = new Date();
  return yr === now.getFullYear()
    ? months[d.getMonth()]
    : `${months[d.getMonth()]} ${yr}`;
}


function fmtDate(iso: string): string {
  const d = parseBackendIso(iso);
  if (!d || Number.isNaN(d.getTime())) return "";
  const months = ["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() !== new Date().getFullYear() ? d.getFullYear() : ""} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`.trim();
}


const KIND_LABELS: Record<string, string> = {
  photo: "Фото",
  file: "Файлы",
  voice: "Голосовые",
  link: "Ссылки",
};


export function UserMediaModal({
  userId, kind, userName, onClose, onNavigateAway,
}: {
  userId: string;
  kind: "photo" | "file" | "voice" | "link";
  userName: string;
  onClose: () => void;
  /** Доп. колбэк перед навигацией (закрыть родительский ProfileLightbox). */
  onNavigateAway?: () => void;
}) {
  const q = useQuery({
    queryKey: ["user-media", userId, kind],
    queryFn: () => fetchUserMedia(userId, kind),
    staleTime: 30_000,
  });

  // Esc + scroll lock
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Блокер нативного browser/iOS context menu внутри media-модалки.
  // ИСКЛЮЧЕНИЕ: фото-вьювер на весь экран (.user-media-photo-viewer) —
  // там нативное меню НУЖНО (Save Image, Copy, Share).
  useEffect(() => {
    const handler = (e: globalThis.MouseEvent) => {
      const tgt = e.target as HTMLElement | null;
      if (!tgt) return;
      if (tgt.closest(".user-media-photo-viewer")) return;  // нативное меню разрешено
      if (tgt.closest(".user-media-modal")) e.preventDefault();
    };
    document.addEventListener("contextmenu", handler, true);
    return () => document.removeEventListener("contextmenu", handler, true);
  }, []);

  // Фото-вьювер
  const [photoIdx, setPhotoIdx] = useState<number | null>(null);
  // Selection mode (TG-style): long-press / right-click → выбор, шапка превращается в toolbar
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const selectionMode = selected.size > 0;
  function toggleSelect(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }
  // Context menu (правый клик / long-press) — TG-стиль:
  // 5 пунктов: Go To Message / Save As / Forward / Delete / Select
  const [ctxMenu, setCtxMenu] = useState<{ item: UserMediaItem; top: number; left: number } | null>(null);
  const navigate = useNavigate();
  function openCtxMenu(item: UserMediaItem, x: number, y: number) {
    const MENU_W = 240, MENU_H = 280, m = 8;
    const top = Math.min(y, window.innerHeight - MENU_H - m);
    const left = Math.min(x, window.innerWidth - MENU_W - m);
    setCtxMenu({ item, top, left });
  }
  function goToMessage(item: UserMediaItem) {
    setCtxMenu(null);
    onClose();
    onNavigateAway?.();
    navigate({ to: "/chat", search: { topic: item.topic_id, jump: item.message_id } as never });
  }
  const qc = useQueryClient();
  const { user } = useAuth();
  const deleteMut = useMutation({
    mutationFn: (messageId: number) => deleteChatMessage(messageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-media", userId, kind] }),
  });
  // (legacy openCtxMenu/goToMessage удалены — selection mode заменил их)
  // Long-press helper: TG-стиль — включает selection mode
  const longPressRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  function onItemTouchStart(e: React.TouchEvent, item: UserMediaItem) {
    if (longPressRef.current) window.clearTimeout(longPressRef.current);
    longPressFiredRef.current = false;
    const t = e.touches[0];
    const x = t.clientX, y = t.clientY;
    longPressRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      openCtxMenu(item, x, y);
      if ("vibrate" in navigator) navigator.vibrate?.(15);
    }, 400);
  }
  function onItemTouchEnd() {
    if (longPressRef.current) { window.clearTimeout(longPressRef.current); longPressRef.current = null; }
  }
  // Bulk-операции для выбранных
  function getSelectedItems(): UserMediaItem[] {
    return items.filter(it => selected.has(it.id));
  }
  function bulkDelete() {
    const sel = getSelectedItems();
    if (!sel.length) return;
    if (!window.confirm(`Удалить ${sel.length} ${sel.length === 1 ? "элемент" : "элементов"}?`)) return;
    Promise.all(sel.map(it => deleteMut.mutateAsync(it.message_id))).then(() => clearSelection());
  }
  function bulkDownload() {
    const sel = getSelectedItems();
    sel.forEach((it, i) => {
      // Создаём <a download> по очереди — браузер скачает каждый
      const a = document.createElement("a");
      a.href = chatAttachmentUrl(it.attachment_id ?? it.id);
      a.download = it.title || `file-${it.id}`;
      a.style.display = "none";
      document.body.appendChild(a);
      setTimeout(() => { a.click(); a.remove(); }, i * 200);
    });
    clearSelection();
  }

  const items = q.data || [];
  // Группировка по месяцу
  const groups: { label: string; items: UserMediaItem[] }[] = [];
  for (const it of items) {
    const lab = fmtMonth(it.created_at);
    const last = groups[groups.length - 1];
    if (last && last.label === lab) last.items.push(it);
    else groups.push({ label: lab, items: [it] });
  }

  return (
    <div className="user-media-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="user-media-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`user-media-head${selectionMode ? " is-selection" : ""}`}>
          {!selectionMode ? (
            <>
              <button type="button" className="user-media-back" onClick={onClose} title="Назад (Esc)">
                <Icon name="chevron_l" size={20}/>
              </button>
              <div className="user-media-title">
                <div>{KIND_LABELS[kind]}</div>
                <div className="user-media-sub">{userName}</div>
              </div>
              <button type="button" className="user-media-close" onClick={onClose}>
                <Icon name="x" size={18}/>
              </button>
            </>
          ) : (
            <>
              <button type="button" className="user-media-back" onClick={clearSelection} title="Отменить выбор">
                <Icon name="x" size={20}/>
              </button>
              <div className="user-media-title">
                <div>Выбрано: {selected.size}</div>
              </div>
              <div className="user-media-sel-actions">
                <button type="button" onClick={bulkDownload} title="Скачать">
                  <Icon name="download" size={18}/>
                </button>
                {user?.is_superuser && (
                  <button type="button" className="is-danger" onClick={bulkDelete} title="Удалить">
                    <Icon name="trash" size={18}/>
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="user-media-body">
          {q.isLoading && (
            <div style={{ padding: 60, display: "grid", placeItems: "center" }}>
              <Spinner size="md" label="Загрузка…"/>
            </div>
          )}
          {!q.isLoading && items.length === 0 && (
            <div style={{ padding: 60, textAlign: "center", color: "var(--mute)" }}>
              Ничего нет
            </div>
          )}
          {!q.isLoading && items.length > 0 && (
            <>
              {groups.map((g, gi) => (
                <div key={gi} className="user-media-group">
                  <div className="user-media-group-label">{g.label}</div>
                  {kind === "photo" && (
                    <div className="user-media-photo-grid">
                      {g.items.map((it) => {
                        const flatIdx = items.findIndex(x => x.id === it.id);
                        const isSelected = selected.has(it.id);
                        return (
                          <button
                            key={it.id}
                            type="button"
                            className={`user-media-photo-cell${isSelected ? " is-selected" : ""}`}
                            onClick={() => {
                              if (selectionMode) toggleSelect(it.id);
                              else if (longPressFiredRef.current) { /* skip */ }
                              else setPhotoIdx(flatIdx);
                            }}
                            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); openCtxMenu(it, e.clientX, e.clientY); }}
                            onTouchStart={(e) => onItemTouchStart(e, it)}
                            onTouchEnd={onItemTouchEnd}
                            onTouchMove={onItemTouchEnd}
                          >
                            <img
                              src={chatAttachmentUrl(it.attachment_id ?? it.id)}
                              alt={it.title}
                              draggable={false}
                              onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); openCtxMenu(it, e.clientX, e.clientY); }}
                              onDragStart={(e) => e.preventDefault()}
                            />
                            {isSelected && (
                              <span className="user-media-check"><Icon name="check" size={16}/></span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {kind === "file" && g.items.map((it) => {
                    const isSelected = selected.has(it.id);
                    return (
                      <a
                        key={it.id}
                        className={`user-media-file-row${isSelected ? " is-selected" : ""}`}
                        href={chatAttachmentUrl(it.attachment_id ?? it.id)}
                        download={it.title}
                        target="_blank" rel="noopener noreferrer"
                        onClick={(e) => {
                          if (selectionMode || longPressFiredRef.current) {
                            e.preventDefault();
                            toggleSelect(it.id);
                          }
                        }}
                        onContextMenu={(e) => { e.preventDefault(); openCtxMenu(it, e.clientX, e.clientY); }}
                        onTouchStart={(e) => onItemTouchStart(e, it)}
                        onTouchEnd={onItemTouchEnd}
                        onTouchMove={onItemTouchEnd}
                      >
                        <div className="user-media-file-icon">
                          {isSelected ? <Icon name="check" size={20}/> : <Icon name="file" size={20}/>}
                        </div>
                        <div className="user-media-file-meta">
                          <div className="user-media-file-name">{it.title}</div>
                          <div className="user-media-file-sub">
                            {fmtSize(it.size_bytes)} · {fmtDate(it.created_at)}
                          </div>
                        </div>
                      </a>
                    );
                  })}
                  {kind === "voice" && g.items.map((it) => {
                    const isSelected = selected.has(it.id);
                    return (
                      <div
                        key={it.id}
                        className={`user-media-voice-row${isSelected ? " is-selected" : ""}`}
                        onClick={(e) => {
                          if (selectionMode) {
                            // не даём audio toggle — клик идёт на selection
                            const tgt = e.target as HTMLElement;
                            if (!tgt.closest(".chat-voice-tg-play, .chat-voice-tg-wave")) {
                              e.preventDefault();
                              toggleSelect(it.id);
                            }
                          }
                        }}
                        onContextMenu={(e) => { e.preventDefault(); openCtxMenu(it, e.clientX, e.clientY); }}
                        onTouchStart={(e) => onItemTouchStart(e, it)}
                        onTouchEnd={onItemTouchEnd}
                        onTouchMove={onItemTouchEnd}
                      >
                        <VoiceMessage
                          src={chatAttachmentUrl(it.attachment_id ?? it.id)}
                          attachmentId={it.attachment_id ?? it.id}
                          durationSec={it.duration_sec}
                        />
                        {it.title && <div className="user-media-voice-trans">📝 {it.title}</div>}
                        <div className="user-media-file-sub">{fmtDate(it.created_at)}</div>
                      </div>
                    );
                  })}
                  {kind === "link" && g.items.map((it) => {
                    const isSelected = selected.has(it.id);
                    return (
                      <div
                        key={`${it.id}-${it.title}`}
                        className={`user-media-link-row${isSelected ? " is-selected" : ""}`}
                        onClick={(e) => {
                          if (selectionMode) {
                            const tgt = e.target as HTMLElement;
                            if (!tgt.closest(".user-media-link-url")) {
                              e.preventDefault();
                              toggleSelect(it.id);
                            }
                          }
                        }}
                        onContextMenu={(e) => { e.preventDefault(); openCtxMenu(it, e.clientX, e.clientY); }}
                        onTouchStart={(e) => onItemTouchStart(e, it)}
                        onTouchEnd={onItemTouchEnd}
                        onTouchMove={onItemTouchEnd}
                      >
                        <div className="user-media-file-icon">
                          {isSelected ? <Icon name="check" size={18}/> : <Icon name="link" size={18}/>}
                        </div>
                        <div className="user-media-file-meta">
                          {it.subtitle && <div className="user-media-file-name">{it.subtitle}</div>}
                          <a href={it.title} target="_blank" rel="noopener noreferrer" className="user-media-link-url">
                            {it.title}
                          </a>
                          <div className="user-media-file-sub">{fmtDate(it.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          )}
        </div>

        {/* Фото-полноэкран — простой <img/> вьювер */}
        {photoIdx !== null && items[photoIdx] && (
          <div
            className="user-media-photo-viewer"
            onClick={() => setPhotoIdx(null)}
          >
            <button
              type="button"
              className="user-media-photo-close"
              onClick={(e) => { e.stopPropagation(); setPhotoIdx(null); }}
            >
              <Icon name="x" size={20}/>
            </button>
            <img
              src={chatAttachmentUrl(items[photoIdx].attachment_id ?? items[photoIdx].id)}
              alt={items[photoIdx].title}
              onClick={(e) => e.stopPropagation()}
            />
            {photoIdx > 0 && (
              <button
                type="button"
                className="user-media-photo-nav is-left"
                onClick={(e) => { e.stopPropagation(); setPhotoIdx(photoIdx - 1); }}
              ><Icon name="chevron_l" size={28}/></button>
            )}
            {photoIdx < items.length - 1 && (
              <button
                type="button"
                className="user-media-photo-nav is-right"
                onClick={(e) => { e.stopPropagation(); setPhotoIdx(photoIdx + 1); }}
              ><Icon name="chevron_r" size={28}/></button>
            )}
          </div>
        )}

        {ctxMenu && (() => {
          const it = ctxMenu.item;
          const url = chatAttachmentUrl(it.attachment_id ?? it.id);
          return (
            <>
              <div className="user-media-ctx-backdrop" onClick={() => setCtxMenu(null)}/>
              <div className="user-media-ctx" style={{ top: ctxMenu.top, left: ctxMenu.left }}>
                <button type="button" className="user-media-ctx-item" onClick={() => goToMessage(it)}>
                  <Icon name="eye" size={16}/> Перейти к сообщению
                </button>
                {it.kind !== "link" && (
                  <a
                    className="user-media-ctx-item"
                    href={url}
                    download={it.title || `file-${it.id}`}
                    onClick={() => setCtxMenu(null)}
                  >
                    <Icon name="download" size={16}/> Сохранить как…
                  </a>
                )}
                <button
                  type="button"
                  className="user-media-ctx-item"
                  onClick={() => {
                    const shareUrl = it.kind === "link" ? it.title : window.location.origin + url;
                    if (navigator.clipboard) navigator.clipboard.writeText(shareUrl).catch(() => {});
                    setCtxMenu(null);
                  }}
                >
                  <Icon name="external" size={16}/> Скопировать ссылку
                </button>
                <button
                  type="button"
                  className="user-media-ctx-item"
                  onClick={() => { toggleSelect(it.id); setCtxMenu(null); }}
                >
                  <Icon name="check" size={16}/> Выбрать
                </button>
                {user?.is_superuser && (
                  <button
                    type="button"
                    className="user-media-ctx-item is-danger"
                    onClick={() => {
                      if (!window.confirm("Удалить сообщение?")) return;
                      deleteMut.mutate(it.message_id);
                      setCtxMenu(null);
                    }}
                  >
                    <Icon name="trash" size={16}/> Удалить
                  </button>
                )}
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
